import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Maximize2, Grid } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LayoutPhoto {
  id: string;
  photo_url: string;
  caption?: string;
  display_order: number;
}

interface LayoutPhotosGalleryProps {
  photos: LayoutPhoto[];
  sequenceTitle?: string;
  className?: string;
}

const LayoutPhotosGallery: React.FC<LayoutPhotosGalleryProps> = ({
  photos,
  sequenceTitle = 'Sequence',
  className
}) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;

      if (e.key === 'ArrowLeft') {
        handlePrevPhoto();
      } else if (e.key === 'ArrowRight') {
        handleNextPhoto();
      } else if (e.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, selectedPhotoIndex, photos.length]);

  const handlePrevPhoto = () => {
    setSelectedPhotoIndex((prev) =>
      prev === 0 ? photos.length - 1 : prev - 1
    );
  };

  const handleNextPhoto = () => {
    setSelectedPhotoIndex((prev) =>
      prev === photos.length - 1 ? 0 : prev + 1
    );
  };

  const openModal = (index: number) => {
    setSelectedPhotoIndex(index);
    setIsModalOpen(true);
  };

  const handleImageLoad = (index: number) => {
    setLoadedImages(prev => new Set(prev).add(index));
  };

  if (!photos || photos.length === 0) {
    return null;
  }

  const sortedPhotos = [...photos].sort((a, b) => a.display_order - b.display_order);

  return (
    <>
      <div className={cn("w-full", className)}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Grid className="h-5 w-5" />
            Layout Photos ({photos.length})
          </h3>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedPhotos.map((photo, index) => (
            <div
              key={photo.id}
              className="group relative aspect-video bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => openModal(index)}
            >
              {/* Loading skeleton */}
              {!loadedImages.has(index) && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse" />
              )}

              <img
                src={photo.photo_url}
                alt={`${sequenceTitle} layout photo ${index + 1}`}
                className={cn(
                  "w-full h-full object-cover transition-opacity duration-300",
                  loadedImages.has(index) ? "opacity-100" : "opacity-0"
                )}
                onLoad={() => handleImageLoad(index)}
              />

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <Maximize2 className="text-white opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8" />
              </div>

              {/* Caption overlay */}
              {photo.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                  <p className="text-white text-sm line-clamp-2">{photo.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Fullscreen Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-0">
          <div className="relative w-full h-full flex flex-col">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent p-4 flex justify-between items-center">
              <div className="text-white">
                <p className="text-sm opacity-75">
                  {selectedPhotoIndex + 1} of {photos.length}
                </p>
                {sortedPhotos[selectedPhotoIndex]?.caption && (
                  <p className="text-lg mt-1">{sortedPhotos[selectedPhotoIndex].caption}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={() => setIsModalOpen(false)}
              >
                <X className="h-6 w-6" />
              </Button>
            </div>

            {/* Main image */}
            <div className="flex-1 flex items-center justify-center relative px-16">
              <img
                src={sortedPhotos[selectedPhotoIndex]?.photo_url}
                alt={`${sequenceTitle} layout photo ${selectedPhotoIndex + 1}`}
                className="max-w-full max-h-full object-contain"
              />

              {/* Navigation buttons */}
              {photos.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/50 backdrop-blur hover:bg-black/70 text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevPhoto();
                    }}
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/50 backdrop-blur hover:bg-black/70 text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextPhoto();
                    }}
                  >
                    <ChevronRight className="h-8 w-8" />
                  </Button>
                </>
              )}
            </div>

            {/* Thumbnail strip */}
            {photos.length > 1 && (
              <div className="bg-black/90 p-4">
                <div className="flex gap-2 justify-center overflow-x-auto max-w-full">
                  {sortedPhotos.map((photo, index) => (
                    <button
                      key={photo.id}
                      onClick={() => setSelectedPhotoIndex(index)}
                      className={cn(
                        "relative shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all",
                        index === selectedPhotoIndex
                          ? "border-white ring-2 ring-white/50"
                          : "border-transparent opacity-60 hover:opacity-100"
                      )}
                    >
                      <img
                        src={photo.photo_url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LayoutPhotosGallery;